import { Icon } from '@iconify/react';
import { Alert } from '@mui/material';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logError } from '../../../../utils/logger';
import {
  ConfirmationModal,
  Loader,
  ModalWrapper,
  SalaryBreakdownTable,
  SectionTitle,
} from '../../../shared/components';
import {
  calculateSalaryBreakdown,
  getSalaryBreakdownRows,
} from '../../../shared/utils/salary.utils';
import EmployeeDocumentsSection from '../../people/components/EmployeeDocumentsSection';
import EmployeeExitSection from '../../people/components/EmployeeExitSection';
import EmployeeLettersSection from '../../people/components/EmployeeLettersSection';
import LeaveBalanceCard from '../../people/components/LeaveBalanceCard';
import EmployeeDetailsCard from '../components/EmployeeDetailsCard';
import EmployeeEditModal from '../components/EmployeeEditModal';
import EmployeePaySlipsSection from '../components/EmployeePayslipsSection';
import { adminResetUserPassword, getEmployeeDetails } from '../employees.service';

const EMPTY_PASSWORD_STATE = { username: null, newPassword: null, employeeId: null };

export default function EmployeeDetailsPage() {
  const { employeeId } = useParams();

  const [employee, setEmployee] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showEdit, setShowEdit] = useState(false);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [resetResult, setResetResult] = useState(EMPTY_PASSWORD_STATE);
  const [showNewPasswordModal, setShowNewPasswordModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchEmployeeDetails = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getEmployeeDetails(employeeId);
      setEmployee(data);
    } catch (err) {
      const message = logError({
        error: err,
        message: 'Failed to fetch employee details',
        code: 'EMPLOYEE_DETAILS_FETCH_FAILED',
      });
      toast.error(message);
      setEmployee({});
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchEmployeeDetails();
  }, [fetchEmployeeDetails]);

  const handleResetPassword = async () => {
    setResetting(true);
    try {
      const data = await adminResetUserPassword(employeeId);
      setResetResult(data);
      setShowConfirmReset(false);
      setShowNewPasswordModal(true);
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to reset employee password',
          error: err,
          code: 'ADMIN_PASSWORD_RESET_FAILED',
        })
      );
      setResetResult(EMPTY_PASSWORD_STATE);
    } finally {
      setResetting(false);
    }
  };

  const breakdown = calculateSalaryBreakdown(employee.currentSalary);
  const rows = getSalaryBreakdownRows(breakdown);

  const { name } = employee || {};

  return loading ? (
    <Loader />
  ) : error ? (
    <Alert severity='error'>{error}</Alert>
  ) : (
    <>
      <Divider />
      <div className='column gap-3'>
        <SectionTitle
          variant='subtitle'
          title={`${name} details`}
          action={
            <div className='row gap-3'>
              <button
                type='button'
                className='btn text-btn text-btn--medium'
                onClick={() => setShowEdit(true)}
                disabled={!employee.id}
              >
                Edit
                <Icon icon='line-md:edit-twotone' width={16} />
              </button>
              <button
                type='button'
                className='btn cta-outlined-btn'
                onClick={() => setShowConfirmReset(true)}
                disabled={resetting}
              >
                Reset Password
              </button>
            </div>
          }
        />
        <div className='column gap-8'>
          <EmployeeDetailsCard employee={employee} />
          <div className='row row--between'>
            <span className='self-start'>Salary Structure</span>
            <SalaryBreakdownTable rows={rows} />
          </div>
          <EmployeePaySlipsSection payslips={employee.payslips} />

          {employee.id && (
            <>
              <LeaveBalanceCard employeeId={employee.id} />
              <EmployeeDocumentsSection employeeId={employee.id} />
              <EmployeeLettersSection employeeId={employee.id} employeeName={employee.name} />
              <EmployeeExitSection
                employeeId={employee.id}
                employeeName={employee.name}
                onCompleted={fetchEmployeeDetails}
              />
            </>
          )}
        </div>
      </div>
      <EmployeeEditModal
        open={showEdit}
        employee={employee}
        onClose={() => setShowEdit(false)}
        onSaved={setEmployee}
      />
      <ConfirmationModal
        open={showConfirmReset}
        title='Reset Employee Password?'
        message={
          <Typography sx={{ fontSize: '1rem', color: 'grey.500', textAlign: 'center' }}>
            This will invalidate the employee’s current password.
            <br />A new password will be generated and must be shared securely.
          </Typography>
        }
        onClose={() => setShowConfirmReset(false)}
        onConfirm={handleResetPassword}
        confirmText='Reset Password'
        loading={resetting}
      />
      <ResetPasswordModal
        open={showNewPasswordModal}
        onClose={() => {
          if (resetting) return;
          setResetResult(EMPTY_PASSWORD_STATE);
          setShowNewPasswordModal(false);
        }}
        data={resetResult}
      />
    </>
  );
}

function ResetPasswordModal({ open, onClose, data }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        `User name: ${data.username}\nNew Password: ${data.newPassword}`
      );
      setCopied(true);
      toast.success('Credentials copied');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('Failed to copy credentials');
    }
  };

  return (
    <ModalWrapper open={open} onClose={onClose} maxWidth='sm'>
      <div className='column gap-3'>
        <Typography sx={{ fontSize: '1.125rem', alignSelf: 'center', fontWeight: 600 }}>
          Reset Password
        </Typography>

        <div className='column'>
          <div className='grid grid--3-cols'>
            <span className='label'>User name</span>
            <span className='value'>{data.username}</span>
          </div>

          <div className='grid grid--3-cols'>
            <span className='label'>Password</span>
            <span className='value'>{data.newPassword}</span>
            <button
              className='btn text-btn text-btn--small justify-self-end'
              onClick={handleCopy}
              disabled={copied}
            >
              <Icon icon='tabler:copy' width={16} />
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}
